import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Message, useToaster } from "rsuite";
import { ClipLoader } from "react-spinners";
import { useChangeAppointmentState } from "../../_store/ChangeAppointment";
import { useHelper } from "../../context/Helper";

type Props = {
  open: boolean;
  caseId?: string;
  onClose: () => void;
};

const QrCodeModal = ({ open, caseId, onClose }: Props) => {
  const { t } = useTranslation("changeappointment");
  const toaster = useToaster();
  const { getMessage } = useHelper();
  const generateQrCode = useChangeAppointmentState((s) => s.generateQrCode);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !caseId) return;
    let cancelled = false;
    let createdUrl: string | null = null;

    setLoading(true);
    generateQrCode(caseId).then((result) => {
      if (cancelled) return;
      if (result.blob) {
        createdUrl = URL.createObjectURL(result.blob);
        setQrUrl(createdUrl);
      } else {
        const msg = result.error ? getMessage(result.error) : "";
        toaster.push(
          <Message showIcon type={"error"} closable>
            <strong>แจ้งเตือน!</strong> {msg}
          </Message>,
          { duration: 5000 }
        );
        onClose();
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseId]);

  useEffect(() => {
    if (!open && qrUrl) {
      URL.revokeObjectURL(qrUrl);
      setQrUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = () => {
    if (!qrUrl) return;
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = `qr-case-${caseId || "kth"}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Modal open={open} onClose={onClose} size="xs" backdrop="static">
      <Modal.Header>
        <Modal.Title>{t("appointmentDetail.qrCode.title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 250 }}
          >
            <ClipLoader color="#000" loading={true} size={60} />
          </div>
        ) : qrUrl ? (
          <div className="text-center">
            <img
              src={qrUrl}
              alt="QR Code"
              style={{ width: 250, height: 250, maxWidth: "100%" }}
            />
            <p className="mt-3 text-muted">
              {t("appointmentDetail.qrCode.hint")}
            </p>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="subtle">
          {t("appointmentDetail.btn.btnClose")}
        </Button>
        <Button
          onClick={handleSave}
          appearance="primary"
          color="green"
          disabled={!qrUrl || loading}
        >
          {t("appointmentDetail.btn.btnSaveQr")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QrCodeModal;
